/**
 * Syntax-highlight XML content — TypeScript port of V1's highlightXml.js.
 *
 * Returns an HTML string with span elements:
 *  .xml-comment — <!-- ... -->         (grey italic in V1)
 *  .xml-tag     — element names        (blue  in V1)
 *  .xml-attr    — attribute names      (orange in V1)
 *  .xml-value   — attribute values     (green  in V1)
 *
 * The CSS classes are already declared in `web/src/index.css` under
 * `.viewer-body .xml-*` selectors (added alongside V1 parity work).
 *
 * SECURITY: the input should be treated as data (not trusted HTML). The
 * function HTML-escapes `&`, `<`, and `>` before applying spans, so the
 * output is safe to inject via `dangerouslySetInnerHTML` into a `<pre>`.
 *
 * IMPLEMENTATION NOTE: regex-based XML highlighting is inherently
 * limited. This implementation uses private-use-area sentinel characters
 * (/) as placeholder delimiters during substitution to
 * prevent subsequent passes from corrupting earlier span injections.
 * This matches V1's visual output without the double-substitution bug
 * that affected a pure chained-replace approach.
 */

// Private-use sentinels — extremely unlikely to appear in real XML content.
// We replace <span...> with class="xml-foo" during processing,
// then swap them back to <span / </span> at the end.
const S_OPEN = '';  // marks start of injected span
const S_CLOSE = ''; // marks end of injected span
const T_END = '';   // marks closing </span>

function span(cls: string, content: string): string {
  return `${S_OPEN}${cls}${S_CLOSE}${content}${T_END}`;
}

export function highlightXml(xml: string): string {
  // 1. Escape HTML special chars so the raw XML is safe to embed.
  let s = xml
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  // 2. Comments (apply first so their content isn't processed further).
  s = s.replace(/(&lt;!--[\s\S]*?--&gt;)/g, (_m, comment: string) =>
    span('xml-comment', comment),
  );

  // 3. Tag names — match &lt; (and optional /) followed by the name.
  //    The name may contain colons (namespace prefix), dots, hyphens.
  s = s.replace(/(&lt;\/?)([\w:.-]+)/g, (_m, lt: string, name: string) =>
    lt + span('xml-tag', name),
  );

  // 4. Attribute names — only match within tag contexts (between &lt; and &gt;).
  //    We do this by scanning for runs of XML tag markup and applying
  //    attribute + value highlighting only within those runs.
  //
  //    A "tag run" is the content between &lt; (or the sentinel-wrapped
  //    close of a tag-name span) and the matching &gt;. We approximate
  //    this by applying attr/value substitution only to the segments
  //    that don't yet contain sentinels (i.e., attribute text outside
  //    already-highlighted spans).
  //
  //    Simpler and correct for well-formed XML: apply attr/value regex to
  //    the full string but use a negative lookbehind to exclude matches
  //    that start immediately after a sentinel (which would be inside an
  //    already-wrapped span).

  // Attribute names: word chars before ="
  s = s.replace(/([\w:.-]+)(=)(")/g, (_m, name: string, eq: string, q: string) => {
    // Skip if the name is one of our sentinel-wrapped class names.
    // Sentinel chars cannot appear in real attribute names so any match
    // that contains them comes from our own span injections.
    if (name === 'xml-comment' || name === 'xml-tag' || name === 'xml-attr' || name === 'xml-value') {
      return name + eq + q;
    }
    return span('xml-attr', name) + eq + q;
  });

  // 5. Attribute values — quoted strings.
  s = s.replace(/("([^"]*)")/g, (_m, quoted: string) =>
    span('xml-value', quoted),
  );

  // 6. Replace sentinels with actual HTML.
  s = s
    .replace(new RegExp(`${S_OPEN}([^${S_CLOSE}]*)${S_CLOSE}`, 'g'), (_m, cls: string) =>
      `<span class="${cls}">`,
    )
    .replace(new RegExp(T_END, 'g'), '</span>');

  return s;
}
